/**
 * crypto.randomUUID() for every browser context.
 *
 * randomUUID only exists in secure contexts (HTTPS or localhost). Opened over
 * plain HTTP on a LAN address (http://192.168.50.75:8081) it is undefined, and
 * @neuro/chat-adapter calls it while the bundle loads, so the whole app
 * rendered a blank page ("crypto.randomUUID is not a function"). Plugin pages
 * and shared packages call it too. crypto.getRandomValues() works in any
 * context, so the polyfill builds the same RFC 4122 v4 UUID from it.
 */

type UUID = `${string}-${string}-${string}-${string}-${string}`;

export function randomUUIDFromRandomValues(): UUID {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Give `target` a randomUUID when the context left it out. Returns whether it
 * installed one (false when randomUUID already exists or there is no crypto).
 */
export function installRandomUUIDPolyfill(target: Crypto | undefined = globalThis.crypto): boolean {
  if (!target || typeof target.randomUUID === 'function' || typeof target.getRandomValues !== 'function') {
    return false;
  }
  Object.defineProperty(target, 'randomUUID', {
    value: randomUUIDFromRandomValues,
    configurable: true,
    writable: true,
  });
  return true;
}
